import { memo, type JSX } from "react";

import { Typography } from "antd";
import { AnimatePresence, motion } from "motion/react";

import { File } from "./file";
import {
    DeleteBlobAction,
    useDeleteBlobMutation,
} from "./queries/useDeleteBlobMutation";
import * as theme from "./theme.css";
import { UploaderFile, UploaderFileRemoved } from "./types";

const { Text } = Typography;

const fileItemPropsEqual = (
    prevProps: ListFileItemProps,
    currentProps: ListFileItemProps
) =>
    Object.is(prevProps.file, currentProps.file) &&
    Object.is(prevProps.deleteDisabled, currentProps.deleteDisabled);

interface ListFileItemProps {
    file: UploaderFile;
    onRemove: () => void;
    deleteDisabled: boolean;
}
const ListFileItem = memo(
    ({ file, onRemove, deleteDisabled }: ListFileItemProps): JSX.Element => {
        const { fileSize, fileName, lastModified, fileType, status } = file;

        if (status === "removed") {
            throw new Error("Removed files should be filtered out");
        }
        const isSuccess = status === "done" || status === "success";
        const isError = status === "error" || status === "missing";

        return (
            <File
                status={status}
                fileName={fileName}
                onDeleteClick={onRemove}
                fileSize={fileSize}
                lastModified={lastModified}
                fileType={fileType}
                keyId={file.keyId}
                deleteDisabled={deleteDisabled}
                url={isSuccess ? file.url : undefined}
                error={isError ? file.error : undefined}
                percent={status === "uploading" ? file.percent : undefined}
            />
        );
    },
    fileItemPropsEqual
);

const TRANSITION_DURATION = 0.3;
interface FileListProps {
    files: UploaderFile[];
    setFiles: (prevFiles: (files: UploaderFile[]) => UploaderFile[]) => void;
    deleteDisabled?: boolean;
    deleteAction: DeleteBlobAction;
    emptyText?: string;
    disabled: boolean;
    hideMissing?: boolean;
}
export const FileList = ({
    files,
    setFiles,
    deleteDisabled = false,
    deleteAction,
    emptyText,
    disabled,
    hideMissing = false,
}: FileListProps): JSX.Element => {
    const { mutateAsync: deleteBlob } = useDeleteBlobMutation(deleteAction);

    const handleDelete = async (fileToRemove: UploaderFile) => {
        if (fileToRemove.status === "removed") {
            throw new Error(
                "It should not be possible to remove an already removed file!"
            );
        }
        const isUploaded =
            fileToRemove.status === "done" || fileToRemove.status === "success";

        const updatedFile: UploaderFileRemoved = {
            status: "removed",
            keyId: fileToRemove.keyId,
            lastModified: fileToRemove.lastModified,
            fileName: fileToRemove.fileName,
            fileSize: fileToRemove.fileSize,
            fileType: fileToRemove.fileType,
            oldId: isUploaded ? fileToRemove.id : undefined,
        };

        setFiles((prevValue) =>
            prevValue.map((file) =>
                updatedFile.keyId === file.keyId ? updatedFile : file
            )
        );

        if (isUploaded) {
            try {
                await deleteBlob({ id: fileToRemove.id });
            } catch {
                // Reverts
                setFiles((prevValue) =>
                    prevValue.map((value) =>
                        value.keyId === fileToRemove.keyId
                            ? fileToRemove
                            : value
                    )
                );
            }
        } else {
            console.warn(
                `The removed file "${fileToRemove.fileName}" was not uploaded`
            );
        }
    };

    const shownFiles = files.filter((file) => {
        const isRemoved = file.status === "removed";
        const isMissing = hideMissing && file.status === "missing";
        if (isMissing) {
            console.debug(`FileList: File "${file.id}" is missing and hidden`);
        }
        return !(isRemoved || isMissing);
    });
    const sortedFiles = shownFiles.sort((a, b) =>
        a.fileName.localeCompare(b.fileName)
    );
    return (
        <div>
            <AnimatePresence initial={false}>
                {sortedFiles.map((file) => (
                    <motion.div
                        key={file.keyId}
                        animate={{ scale: 1, opacity: 1, height: 64 }}
                        initial={{ scale: 0.8, opacity: 0, height: 0 }}
                        exit={{ scale: 0.8, opacity: 0, height: 0 }}
                        transition={{ duration: TRANSITION_DURATION }}
                        className={theme.fileListItem}
                        layout
                    >
                        <ListFileItem
                            key={file.keyId}
                            deleteDisabled={deleteDisabled}
                            onRemove={() => void handleDelete(file)}
                            file={file}
                        />
                    </motion.div>
                ))}
            </AnimatePresence>
            {disabled && sortedFiles.length === 0 && !!emptyText && (
                <Text>{emptyText}</Text>
            )}
        </div>
    );
};
